

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Alert,
  Typography,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, LinearProgress,
  Chip, Button, TextField, IconButton,
  InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Stack, Switch, Tooltip
} from '@mui/material';
import {
  Add, Edit, Delete, ContentCopy, Link as LinkIcon,
  PlaylistAdd, DeleteOutline, SearchRounded,
  ExpandMore, ExpandLess, KeyboardArrowUp, KeyboardArrowDown, OpenInNew, WarningAmber
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getCvJobs,
  createCvJob,
  updateCvJob,
  deleteCvJob,
  getRestaurantSlug,
  getCvForms,
  createCvForm,
} from '../../../../services/api';
import ConfirmDialog from '../../../common/ConfirmDialog';

const STATUS_LABELS = {
  open: 'Ativas',
  closed: 'Inativas',
};

const STATUS_CHIP_LABELS = {
  open: 'Ativa',
  closed: 'Inativa',
};

const getJobStatus = (job = {}) => (
  (job.status || (job.active || job.is_active || job.ativo ? 'open' : 'closed')).toLowerCase() === 'open'
    ? 'open'
    : 'closed'
);

const isJobActive = (job = {}) => getJobStatus(job) === 'open';

/**
 * Tenta ler o ID do restaurante do localStorage a partir de várias chaves comuns.
 * Inclui 'mesaAtual' como uma chave prioritária e fallbacks para 'mockDatabase' e 'user'.
 * @returns {string | null} O ID do restaurante encontrado, ou null.
 */
function readRestaurantIdFromStorage() {
  const keys = [
    'restaurantId','restauranteId','restaurant_id','restaurante_id',
    'currentRestaurantId','selectedRestaurantId',
    'mesaAtual', // Chave principal
  ];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v && String(v).trim()) return String(v).trim();
  }

  // Fallback 1: mockDatabase
  const mockDbString = localStorage.getItem('mockDatabase');
  if (mockDbString) {
    try {
      const mockDb = JSON.parse(mockDbString);
      if (Array.isArray(mockDb) && mockDb.length > 0 && mockDb[0] && mockDb[0].id) {
         const id = mockDb[0].id_numerico ?? mockDb[0].restaurant_id ?? mockDb[0].id;
         return String(id);
      }
    } catch (e) {
      console.warn('Falha ao ler mockDatabase do localStorage', e);
    }
  }

  // Fallback 2: objeto 'user'
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const v = user?.restaurant_id ?? user?.restaurante_id ?? user?.restaurant?.id ?? null;
    return v != null ? String(v) : null;
  } catch { return null; }
}

/**
 * Componente de página (Admin) para gerenciar Vagas (Jobs).
 * Permite criar, listar, editar, excluir vagas e gerenciar suas etapas associadas.
 */
export default function Jobs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // Guarda o objeto Job ao editar
  const [restaurantSlug, setRestaurantSlug] = useState(null);

  // Lista de formulários/etapas globais (para o <select> do modal)
  const [availableForms, setAvailableForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  // Estado do formulário do modal (Nova Vaga / Editar Vaga)
  const [form, setForm] = useState({
    title: '',
    area: '',
    seniority: '',
    salary_floor: '',
    salary_ceiling: '',
    form_ids: [], // Array de IDs de cv_form
  });

  const [deleteId, setDeleteId] = useState(null);
  const [createStageOpen, setCreateStageOpen] = useState(false);
  const [newStageTitle, setNewStageTitle] = useState('');
  const [creatingStage, setCreatingStage] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedJobs, setExpandedJobs] = useState({});
  const [jobStagesMap, setJobStagesMap] = useState({});
  const [jobStagesLoadingMap, setJobStagesLoadingMap] = useState({});
  const [jobStagesSavingMap, setJobStagesSavingMap] = useState({});
  const [jobStatusSavingMap, setJobStatusSavingMap] = useState({});

  /**
   * Converte um valor (possivelmente string) para número ou string.
   * Usado para IDs.
   * @param {*} value - O valor (ID) a ser normalizado.
   * @returns {string | number}
   */
  const parseStageId = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const n = Number(value);
    return Number.isNaN(n) ? value : n;
  };

  /**
   * Limpa um array de IDs, removendo nulos/vazios.
   * @param {Array<*>} ids - Array de IDs de formulário.
   * @returns {Array<string|number>}
   */
  const collectValidStageIds = (ids) => {
    if (!Array.isArray(ids)) return [];
    return ids
      .map((v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isNaN(n) ? v : n; // Retorna o ID (string ou número)
      })
      .filter((v) => v !== null);
  };

  /**
   * Ordena a lista de formulários globais (para o <select>).
   */
  const sortForms = useCallback((list) => {
    return [...list].sort((a, b) => {
      const pa = a?.position ?? a?.order ?? a?.display_order ?? a?.sequence ?? 0;
      const pb = b?.position ?? b?.order ?? b?.display_order ?? b?.sequence ?? 0;
      if (pa !== pb) return pa - pb;
      return String(a?.created_at ?? '').localeCompare(String(b?.created_at ?? ''));
    });
  }, []);

  /**
   * Faz o parse de valores monetários em formatos comuns (pt-BR e en-US).
   * @param {string|number|null|undefined} value
   * @returns {number|null}
   */
  const parseMoneyToNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    let raw = String(value).trim();
    if (!raw) return null;

    raw = raw.replace(/[^\d,.-]/g, '');
    if (!raw) return null;

    const hasComma = raw.includes(',');
    const hasDot = raw.includes('.');

    if (hasComma && hasDot) {
      raw = raw.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
      raw = raw.replace(',', '.');
    } else if (hasDot) {
      const dotCount = (raw.match(/\./g) || []).length;
      if (dotCount > 1 || /^-?\d+\.\d{3}$/.test(raw)) {
        raw = raw.replace(/\./g, '');
      }
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  /**
   * Aplica máscara monetária no padrão pt-BR para exibir no input.
   * @param {string|number|null|undefined} value
   * @returns {string}
   */
  const formatMoneyInput = (value) => {
    const parsed = parseMoneyToNumber(value);
    if (parsed === null) return '';
    return parsed.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /**
   * Normaliza o texto digitado no input de salário sem forçar formatação.
   * Mantém apenas números, sinal e um separador decimal.
   * @param {string} value
   * @returns {string}
   */
  const normalizeMoneyTyping = (value) => {
    if (value === null || value === undefined) return '';
    let raw = String(value).replace(/[^\d,.-]/g, '');
    raw = raw.replace(/(?!^)-/g, '');
    if (!raw) return '';

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const decimalIndex = Math.max(lastComma, lastDot);
    const hasDecimal = decimalIndex >= 0;

    if (!hasDecimal) return raw;

    const intPart = raw.slice(0, decimalIndex).replace(/[.,]/g, '');
    const decPart = raw.slice(decimalIndex + 1).replace(/[.,]/g, '').slice(0, 2);
    const sign = intPart.startsWith('-') ? '-' : '';
    const intDigits = intPart.replace('-', '');

    return `${sign}${intDigits}${decPart.length ? `,${decPart}` : ','}`;
  };

  /**
   * Converte para um formato editável sem separador de milhar.
   * @param {string|number|null|undefined} value
   * @returns {string}
   */
  const toEditableMoneyInput = (value) => {
    const parsed = parseMoneyToNumber(value);
    if (parsed === null) return '';
    return parsed.toLocaleString('pt-BR', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const handleMoneyFocus = (field) => {
    setForm((prev) => ({ ...prev, [field]: toEditableMoneyInput(prev[field]) }));
  };

  const handleMoneyChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: normalizeMoneyTyping(value) }));
  };

  const handleMoneyBlur = (field) => {
    setForm((prev) => ({ ...prev, [field]: formatMoneyInput(prev[field]) }));
  };

  /**
   * Carrega a lista principal de vagas (jobs).
   */
  const load = async () => {
    setLoading(true);
    try {
      const data = await getCvJobs();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRows([]);
      toast.error('Erro ao carregar vagas');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  /**
   * Lê o filtro de status da URL.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = (params.get('status') || '').toLowerCase();
    setStatusFilter(statusParam === 'draft' ? 'closed' : statusParam);
  }, [location.search]);

  // --- Lógica para buscar o Slug do Restaurante ---

  // 1. Tenta ler do localStorage
  const restaurantIdFromStorage = useMemo(() => readRestaurantIdFromStorage(), []);
  // 2. Tenta inferir pelo restaurante_id das vagas carregadas
  const restaurantIdFromRows = useMemo(() => {
    if (!rows?.length) return null;
    const ids = [...new Set(
      rows
        .map((r) => r?.restaurante_id ?? r?.restaurant_id)
        .filter((v) => v !== null && v !== undefined)
        .map(String)
    )];
    return ids.length === 1 ? ids[0] : null;
  }, [rows]);

  // 3. Tenta ler o slug direto das vagas (se a API o incluir)
  const restaurantSlugFromRows = useMemo(() => {
    if (!rows?.length) return null;
    const slugs = [...new Set(
      rows
        .map((r) => r?.restaurante_slug ?? r?.restaurant_slug ?? r?.slug_restaurante ?? r?.slug)
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .filter((v) => v)
    )];
    return slugs.length === 1 ? slugs[0] : null;
  }, [rows]);

  const resolvedRestaurantId = restaurantIdFromStorage ?? restaurantIdFromRows ?? null;

  // 4. Efeito final para buscar o slug via API se necessário
  useEffect(() => {
    if (restaurantSlugFromRows) {
      setRestaurantSlug(restaurantSlugFromRows);
      return;
    }
    if (!resolvedRestaurantId) {
      setRestaurantSlug(null);
      return;
    }

    let active = true;
    getRestaurantSlug(resolvedRestaurantId)
      .then((d) => {
        if (!active) return;
        // Normaliza a resposta da API
        const value = typeof d === 'string'
          ? d.trim()
          : (d?.slug ?? d?.data?.slug ?? '').trim();
        setRestaurantSlug(value || null);
      })
      .catch(() => {
        if (active) setRestaurantSlug(null);
      });

    return () => { active = false; };
  }, [restaurantSlugFromRows, resolvedRestaurantId]);

  // --- Manipuladores de Ação ---

  /**
   * Abre o modal para criar uma nova vaga.
   * Carrega a lista de formulários/etapas globais disponíveis.
   */
  const openNew = async () => {
    setEditing(null);
    setForm({
      title: '',
      area: '',
      seniority: '',
      salary_floor: '',
      salary_ceiling: '',
      form_ids: [], // Inicia com array de IDs de formulário vazio
    });
    setDialogOpen(true);

    setLoadingForms(true);
    try {
      const all = await getCvForms(); // Busca global (sem jobId)
      setAvailableForms(Array.isArray(all) ? sortForms(all) : []);
    } catch (e) {
      console.error(e);
      setAvailableForms([]);
      toast.error('Erro ao carregar etapas');
    } finally {
      setLoadingForms(false);
    }
  };

  /**
   * Abre o modal para editar uma vaga existente.
   * Carrega as etapas globais (para o <select>) e as etapas já
   * vinculadas a esta vaga (para preencher os <select>s).
   * @param {object} job - O objeto da vaga a ser editada.
   */
  const openEdit = async (job) => {
    setEditing(job);
    setForm({
      title: job.title || '',
      area: job.area || '',
      seniority: job.seniority || '',
      salary_floor: formatMoneyInput(job.salary_floor),
      salary_ceiling: formatMoneyInput(job.salary_ceiling),
      form_ids: [], // Será preenchido abaixo
    });
    setDialogOpen(true);

    setLoadingForms(true);
    try {
      const [allForms, jobForms] = await Promise.all([
        getCvForms(),          // Lista global de cv_form
        getCvForms(job.id),    // Lista de cv_form vinculados à vaga, já ordenados
      ]);

      setAvailableForms(Array.isArray(allForms) ? sortForms(allForms) : []);

      // Mapeia os IDs dos formulários (cv_form.id) vinculados
      const orderedFormIds = (Array.isArray(jobForms) ? jobForms : [])
        .map((f) => f?.id)
        .filter(Boolean)
        .map(parseStageId);

      setForm((prev) => ({ ...prev, form_ids: orderedFormIds }));
    } catch (err) {
      console.error('Erro ao carregar etapas:', err);
      setAvailableForms([]);
      setForm((prev) => ({ ...prev, form_ids: [] }));
      toast.error('Erro ao carregar etapas da vaga.');
    } finally {
      setLoadingForms(false);
    }
  };

  /**
   * Converte valor monetário (string) para número ou null.
   * @param {string|number} v - O valor (ex: "1500,50").
   * @returns {number | null}
   */
  const toNumberOrNull = (v) => {
    return parseMoneyToNumber(v);
  };

  /**
   * Salva a vaga (criação ou edição).
   * Na criação, faz um POST da vaga e depois um PUT para sincronizar as etapas (form_ids).
   * Na edição, faz um PUT único com os form_ids.
   */
  const handleSave = async () => {
    if (!form.title) {
      toast.warning('Informe um título para a vaga.');
      return;
    }
    const validFormIds = collectValidStageIds(form.form_ids);
    if (validFormIds.length === 0) {
      toast.warning('Selecione ao menos uma etapa para a vaga.');
      return;
    }

    const payloadBase = {
      title: form.title,
      area: form.area || null,
      seniority: form.seniority || null,
      salary_floor: toNumberOrNull(form.salary_floor),
      salary_ceiling: toNumberOrNull(form.salary_ceiling),
    };

    let savedJob = null;
    try {
      if (editing) {
        // UPDATE: Envia 'form_ids' para o backend sincronizar
        const updated = await updateCvJob(editing.id, { ...payloadBase, form_ids: validFormIds });
        const updatedData = updated?.data && typeof updated.data === 'object' ? updated.data : updated;
        savedJob = {
          ...payloadBase,
          ...(updatedData && typeof updatedData === 'object' ? updatedData : {}),
          id: editing.id,
          stages_count: validFormIds.length,
        };
        toast.success('Vaga atualizada com sucesso!');
      } else {
        // CREATE: 1. Cria a vaga
        const created = await createCvJob({ ...payloadBase });
        const newJobId = created?.id || created?.data?.id || null;

        if (!newJobId) {
          toast.warning('Vaga criada, mas não consegui obter o ID para vincular etapas.');
        } else {
          // 2. Faz um PUT para sincronizar as etapas
          try {
            await updateCvJob(newJobId, { form_ids: validFormIds });
          } catch (e) {
            console.error('Falha ao sincronizar etapas após criar vaga:', e);
            toast.error('Vaga criada, mas não foi possível vincular as etapas. Edite a vaga e salve novamente.');
          }
        }
        toast.success('Vaga criada com sucesso!');
      }

      setDialogOpen(false);
      await load();
      if (savedJob) {
        setRows((current) => current.map((job) => (
          String(job?.id) === String(savedJob.id)
            ? { ...job, ...savedJob }
            : job
        )));
        await loadJobStages(savedJob.id, { force: true });
      }
    } catch (e) {
      console.error(e);
      toast.error('Erro ao salvar vaga.');
    }
  };

  /**
   * Atualiza o ID do formulário (form_id) em um índice específico na lista de etapas da vaga.
   * @param {number} index - O índice do <select> (0, 1, 2...).
   * @param {string|number} value - O ID do cv_form selecionado.
   */
  const handleStageSelectChange = (index, value) => {
    setForm((prev) => {
      const list = Array.isArray(prev.form_ids) ? [...prev.form_ids] : [];
      if (index < 0 || index >= list.length) return prev;
      if (value === '' || value === null || value === undefined) {
        list[index] = '';
      } else {
        const n = Number(value);
        list[index] = Number.isNaN(n) ? value : n; // guarda form_id
      }
      return { ...prev, form_ids: list };
    });
  };

  /**
   * Adiciona um novo <select> (vazio) para etapa no modal.
   */
  const handleAddStage = () => {
    setForm((prev) => {
      const list = Array.isArray(prev.form_ids) ? [...prev.form_ids] : [];
      const hasEmpty = list.some((v) => v === '' || v === null || v === undefined);
      if (hasEmpty) return prev; // Impede adicionar se já houver um vazio
      return { ...prev, form_ids: [...list, ''] };
    });
  };

  /**
   * Remove um <select> de etapa (pelo índice) do modal.
   * @param {number} index - O índice da etapa a remover.
   */
  const handleRemoveStage = (index) => {
    setForm((prev) => ({
      ...prev,
      form_ids: (Array.isArray(prev.form_ids) ? prev.form_ids : []).filter((_, i) => i !== index),
    }));
  };

  /**
   * Move uma etapa para cima/baixo na ordem do modal de edição da vaga.
   * @param {number} index - Índice atual da etapa.
   * @param {number} direction - -1 para cima, +1 para baixo.
   */
  const handleMoveStage = (index, direction) => {
    setForm((prev) => {
      const list = Array.isArray(prev.form_ids) ? [...prev.form_ids] : [];
      const target = index + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...prev, form_ids: list };
    });
  };

  /**
   * Abre o dialog "Criar nova etapa" (atalho).
   */
  const openCreateStageDialog = () => {
    setNewStageTitle('');
    setCreateStageOpen(true);
  };
  const handleCloseCreateStageDialog = () => {
    if (creatingStage) return;
    setCreateStageOpen(false);
    setNewStageTitle('');
  };

  /**
   * Cria uma nova etapa (cv_form) via API.
   * Se estiver editando uma vaga, já vincula a etapa a essa vaga.
   * Recarrega a lista 'availableForms' e adiciona o novo ID ao <select> da vaga.
   */
  const handleCreateStage = async () => {
    const trimmed = newStageTitle.trim();
    if (!trimmed) {
      toast.warning('Informe um título para a etapa.');
      return;
    }
    setCreatingStage(true);
    try {
      // Cria o cv_form. Se estiver editando uma vaga, já associa (cria cv_stage)
      const created = await createCvForm({ title: trimmed, job_id: editing?.id });
      toast.success('Etapa criada com sucesso.');

      // Recarrega a lista de formulários globais
      const all = await getCvForms();
      setAvailableForms(Array.isArray(all) ? sortForms(all) : []);

      // Adiciona o ID do novo formulário (created.id) ao final da lista de 'form_ids' da vaga
      setForm((prev) => {
        const list = Array.isArray(prev.form_ids) ? [...prev.form_ids] : [];
        if (created?.id && !list.some((v) => String(v) === String(created.id))) {
          return { ...prev, form_ids: [...list, created.id] };
        }
        return { ...prev, form_ids: list };
      });

      setCreateStageOpen(false);
      setNewStageTitle('');

      if (editing?.id) {
        await Promise.all([
          load(),
          loadJobStages(editing.id, { force: true }),
        ]);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar etapa.');
    } finally {
      setCreatingStage(false);
    }
  };

  /**
   * Manipulador para deletar uma vaga (via ConfirmDialog).
   */
  const handleDelete = async () => {
    try {
      await deleteCvJob(deleteId);
      toast.success('Vaga removida com sucesso.');
      await load();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao remover vaga. Verifique se ela possui candidaturas associadas.');
    } finally {
      setDeleteId(null);
    }
  };

  /**
   * Navega para a página de gerenciamento de etapas,
   * pré-filtrando pela vaga (jobId).
   * @param {string} jobId - ID da vaga.
   */
  const loadJobStages = useCallback(async (jobId, options = {}) => {
    const normalizedJobId = String(jobId);
    const { force = false } = options;
    if (!force && Array.isArray(jobStagesMap[normalizedJobId])) return;

    setJobStagesLoadingMap((prev) => ({ ...prev, [normalizedJobId]: true }));
    try {
      const data = await getCvForms(jobId);
      const ordered = Array.isArray(data) ? data : [];
      setJobStagesMap((prev) => ({ ...prev, [normalizedJobId]: ordered }));
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar etapas da vaga.');
    } finally {
      setJobStagesLoadingMap((prev) => ({ ...prev, [normalizedJobId]: false }));
    }
  }, [jobStagesMap]);

  const handleManageStages = (jobId) => {
    const normalizedJobId = String(jobId);
    const willOpen = !expandedJobs[normalizedJobId];
    setExpandedJobs((prev) => ({ ...prev, [normalizedJobId]: willOpen }));
    if (willOpen) loadJobStages(jobId);
  };

  const handleReorderJobStage = async (jobId, index, direction) => {
    const normalizedJobId = String(jobId);
    const currentList = Array.isArray(jobStagesMap[normalizedJobId]) ? jobStagesMap[normalizedJobId] : [];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= currentList.length) return;

    const reordered = [...currentList];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    const formIds = collectValidStageIds(reordered.map((stage) => stage?.id));
    if (!formIds.length) return;

    setJobStagesMap((prev) => ({ ...prev, [normalizedJobId]: reordered }));
    setJobStagesSavingMap((prev) => ({ ...prev, [normalizedJobId]: true }));
    try {
      await updateCvJob(jobId, { form_ids: formIds });
      toast.success('Ordem das etapas atualizada.');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar a nova ordem das etapas.');
      setJobStagesMap((prev) => ({ ...prev, [normalizedJobId]: currentList }));
      await loadJobStages(jobId, { force: true });
    } finally {
      setJobStagesSavingMap((prev) => ({ ...prev, [normalizedJobId]: false }));
    }
  };

  const handleGoToStagesPage = (jobId) => {
    if (!jobId) return;
    navigate(`/cv/etapas?jobId=${jobId}`);
  };

  /**
   * Copia o link de candidatura individual de uma vaga (pública).
   * @param {string} jobId - ID da vaga.
   */
  const copyToClipboard = (jobId) => {
    const link = `${window.location.origin}/cv/aplicar/${jobId}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Link da vaga copiado!'),
      (err) => { toast.error('Não foi possível copiar o link.'); console.error(err); }
    );
  };

  /**
   * Copia o link da página pública de listagem de vagas (baseado no slug).
   */
  const copyPublicJobsLink = () => {
    if (!restaurantSlug) {
      toast.error('Slug do restaurante não encontrado.');
      return;
    }
    const link = `${window.location.origin}/cv/vagas/${restaurantSlug}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Link público copiado!'),
      () => toast.error('Não foi possível copiar o link.')
    );
  };

  const handleToggleActive = async (job, nextActive) => {
    if (!job?.id) return;
    const jobId = job.id;
    const nextStatus = nextActive ? 'open' : 'closed';
    const previousRows = rows;

    setRows((current) => current.map((item) => (
      item.id === jobId
        ? { ...item, status: nextStatus, active: nextActive, is_active: nextActive, ativo: nextActive }
        : item
    )));
    setJobStatusSavingMap((prev) => ({ ...prev, [jobId]: true }));

    try {
      const updated = await updateCvJob(jobId, { active: nextActive });
      const updatedActive = isJobActive(updated);
      setRows((current) => current.map((item) => (
        item.id === jobId
          ? { ...item, ...updated, status: getJobStatus(updated), active: updatedActive, is_active: updatedActive, ativo: updatedActive }
          : item
      )));
      toast.success(nextActive ? 'Vaga ativada.' : 'Vaga inativada.');
    } catch (error) {
      console.error(error);
      setRows(previousRows);
      toast.error('Não foi possível atualizar o status da vaga.');
    } finally {
      setJobStatusSavingMap((prev) => ({ ...prev, [jobId]: false }));
    }
  };

  /**
   * Lista de vagas filtrada (localmente) pela busca e filtro de status.
   */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statusValue = statusFilter.trim();
    const list = [...rows].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    return list.filter((r) => {
      const currentStatus = getJobStatus(r);
      const matchesStatus = !statusValue || currentStatus === statusValue;
      if (!matchesStatus) return false;
      if (!q) return true;
      const statusLabel = (STATUS_CHIP_LABELS[currentStatus] || '').toLowerCase();
      return (
        (r.title || '').toLowerCase().includes(q) ||
        currentStatus.includes(q) ||
        statusLabel.includes(q) ||
        (r.area || '').toLowerCase().includes(q) ||
        (r.seniority || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter]);

  const activeJobsCount = useMemo(
    () => rows.filter((r) => isJobActive(r)).length,
    [rows]
  );

  const renderJobStagesPanel = (jobId) => {
    const normalizedJobId = String(jobId);
    const stages = Array.isArray(jobStagesMap[normalizedJobId]) ? jobStagesMap[normalizedJobId] : [];
    const loadingStages = Boolean(jobStagesLoadingMap[normalizedJobId]);
    const savingStages = Boolean(jobStagesSavingMap[normalizedJobId]);

    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'grey.50',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Etapas da vaga
          </Typography>
          {savingStages && (
            <Typography variant="caption" color="text.secondary">
              Salvando ordem...
            </Typography>
          )}
        </Stack>

        {loadingStages && (
          <Typography variant="body2" color="text.secondary">
            Carregando etapas...
          </Typography>
        )}

        {!loadingStages && stages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nenhuma etapa vinculada a esta vaga.
          </Typography>
        )}

        {!loadingStages && stages.length > 0 && (
          <Stack spacing={1}>
            {stages.map((stage, index) => (
              <Stack
                key={`job-stage-${jobId}-${stage?.id ?? index}`}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {index + 1}. {stage?.title || `Etapa ${index + 1}`}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.25} alignItems="center">
                  <Tooltip title="Mover para cima">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleReorderJobStage(jobId, index, -1)}
                        disabled={savingStages || index === 0}
                        aria-label="Mover para cima"
                      >
                        <KeyboardArrowUp fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Mover para baixo">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleReorderJobStage(jobId, index, 1)}
                        disabled={savingStages || index === stages.length - 1}
                        aria-label="Mover para baixo"
                      >
                        <KeyboardArrowDown fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Ir para etapas">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleGoToStagesPage(jobId)}
                        aria-label="Ir para etapas"
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    );
  };

  // --- Variáveis derivadas para o Modal ---
  const validFormIds = collectValidStageIds(form.form_ids); // IDs de cv_form
  const selectedStageIdsSet = new Set(validFormIds.map(String));
  const hasEmptyStageSelection = Array.isArray(form.form_ids)
    && form.form_ids.some((v) => v === '' || v === null || v === undefined);

  // -------------------- Render --------------------
  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          background: 'linear-gradient(135deg, #f8fbff 0%, #f2f7ff 100%)',
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Gerenciamento de Vagas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Gerencie vagas, etapas e links de candidatura com mais agilidade.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${rows.length} ${rows.length === 1 ? 'vaga cadastrada' : 'vagas cadastradas'}`}
                />
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${activeJobsCount} ${activeJobsCount === 1 ? 'vaga ativa' : 'vagas ativas'}`}
                />
              </Stack>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Tooltip
                title={restaurantSlug ? 'Copiar link público das vagas' : 'Slug do restaurante não encontrado'}
              >
                <Box component="span" sx={{ display: 'block', width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    startIcon={<LinkIcon />}
                    variant="outlined"
                    onClick={copyPublicJobsLink}
                    disabled={!restaurantSlug}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { sm: 150 },
                    }}
                  >
                    Link Público
                  </Button>
                </Box>
              </Tooltip>

              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={openNew}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 150 },
                }}
              >
                Nova vaga
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar por título, ativo, área ou senioridade"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {statusFilter && (
              <Chip
                label={`Filtro ativo: ${STATUS_LABELS[statusFilter] || statusFilter}`}
                onDelete={() => navigate('/cv/jobs')}
                color="info"
                variant="outlined"
                size="small"
                sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {loading && <LinearProgress />}

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table
              size="small"
              sx={{
                '& th:first-of-type, & td:first-of-type': {
                  pl: 3,
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ minWidth: 220, fontWeight: 700 }}>Título</TableCell>
                  <TableCell sx={{ minWidth: 160, fontWeight: 700 }}>Ativa</TableCell>
                  <TableCell align="center" sx={{ width: 120, fontWeight: 700 }}>Etapas</TableCell>
                  <TableCell sx={{ minWidth: 190, fontWeight: 700 }}>Link Individual</TableCell>
                  <TableCell align="right" sx={{ width: 140, fontWeight: 700 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => {
                  const stagesCount = r.stages_count ?? 0;
                  const status = getJobStatus(r);
                  const active = status === 'open';
                  const savingStatus = Boolean(jobStatusSavingMap[r.id]);
                  const details = [r.area, r.seniority].filter(Boolean).join(' | ');
                  const isExpanded = Boolean(expandedJobs[String(r.id)]);
                  return (
                    <Fragment key={r.id}>
                      <TableRow
                        hover
                        onClick={() => handleManageStages(r.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Stack spacing={0.2}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {r.title}
                            </Typography>
                            {details && (
                              <Typography variant="caption" color="text.secondary">
                                {details}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                              size="small"
                              checked={active}
                              disabled={savingStatus}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleToggleActive(r, e.target.checked)}
                              inputProps={{ 'aria-label': `Alternar vaga ${r.title || r.id}` }}
                            />
                            <Typography variant="caption" color={active ? 'success.main' : 'text.secondary'}>
                              {STATUS_CHIP_LABELS[status]}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" variant="outlined" label={stagesCount} />
                        </TableCell>
                        <TableCell>
                          {active ? (
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(r.id);
                              }}
                              startIcon={<ContentCopy fontSize="small" />}
                              sx={{ textTransform: 'none', fontWeight: 600, p: 0 }}
                            >
                              Copiar link
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Disponível apenas para vagas ativas
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={isExpanded ? 'Ocultar etapas' : 'Gerenciar etapas'}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleManageStages(r.id);
                              }}
                              aria-label={isExpanded ? 'Ocultar etapas' : 'Gerenciar etapas'}
                            >
                              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar Dados da Vaga">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(r);
                              }}
                              aria-label="Editar Dados da Vaga"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir Vaga">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(r.id);
                              }}
                              aria-label="Excluir Vaga"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 1.5 }}>
                            {renderJobStagesPanel(r.id)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}

                {!loading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography sx={{ py: 3 }} color="text.secondary">
                        {search ? 'Nenhuma vaga encontrada para sua busca.' : 'Nenhuma vaga cadastrada.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.25 }}>
          <Stack spacing={1.25}>
            {filtered.map((r) => {
              const stagesCount = r.stages_count ?? 0;
              const status = getJobStatus(r);
              const active = status === 'open';
              const savingStatus = Boolean(jobStatusSavingMap[r.id]);
              const details = [r.area, r.seniority].filter(Boolean).join(' | ');
              const isExpanded = Boolean(expandedJobs[String(r.id)]);
              return (
                <Paper
                  key={`mobile-${r.id}`}
                  variant="outlined"
                  onClick={() => handleManageStages(r.id)}
                  sx={{ p: 1.5, borderRadius: 1.5, cursor: 'pointer' }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {r.title}
                        </Typography>
                        {details && (
                          <Typography variant="caption" color="text.secondary">
                            {details}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Ativa
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color={active ? 'success.main' : 'text.secondary'}>
                          {STATUS_CHIP_LABELS[status]}
                        </Typography>
                        <Switch
                          size="small"
                          checked={active}
                          disabled={savingStatus}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleToggleActive(r, e.target.checked)}
                          inputProps={{ 'aria-label': `Alternar vaga ${r.title || r.id}` }}
                        />
                      </Stack>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Etapas
                      </Typography>
                      <Chip size="small" variant="outlined" label={stagesCount} />
                    </Stack>

                    <Box>
                      {active ? (
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(r.id);
                          }}
                          startIcon={<ContentCopy fontSize="small" />}
                          sx={{ textTransform: 'none', fontWeight: 600, p: 0 }}
                        >
                          Copiar link
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Disponível apenas para vagas ativas
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={isExpanded ? 'Ocultar etapas' : 'Gerenciar etapas'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManageStages(r.id);
                          }}
                          aria-label={isExpanded ? 'Ocultar etapas' : 'Gerenciar etapas'}
                        >
                          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar Dados da Vaga">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(r);
                          }}
                          aria-label="Editar Dados da Vaga"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir Vaga">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(r.id);
                          }}
                          aria-label="Excluir Vaga"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {isExpanded && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        {renderJobStagesPanel(r.id)}
                      </Box>
                    )}
                  </Stack>
                </Paper>
              );
            })}

            {!loading && filtered.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography align="center" color="text.secondary">
                  {search ? 'Nenhuma vaga encontrada para sua busca.' : 'Nenhuma vaga cadastrada.'}
                </Typography>
              </Paper>
            )}
          </Stack>
        </Box>
      </Paper>
      {/* Dialog: Nova Vaga / Editar Vaga */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            display: 'flex',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>{editing ? 'Editar Vaga' : 'Nova Vaga'}</DialogTitle>

        <DialogContent
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pt: 2,
            minWidth: 0,
            px: 3,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <TextField
              label="Título da vaga"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 200) })}
              autoFocus
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 0.75 }}
            />
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Área (opcional)"
              value={form.area}
              onChange={(e) => setForm({ ...form, area: e.target.value.slice(0, 120) })}
              fullWidth
            />
            <TextField
              label="Senioridade (opcional)"
              value={form.seniority}
              onChange={(e) => setForm({ ...form, seniority: e.target.value.slice(0, 50) })}
              fullWidth
            />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Etapas desta vaga
            </Typography>

            {loadingForms && (
              <Typography variant="body2" color="text.secondary">
                Carregando etapas disponíveis...
              </Typography>
            )}

            {!loadingForms && form.form_ids.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nenhuma etapa adicionada. Clique em "Adicionar etapa" para selecionar uma etapa
                existente ou em "Criar etapa" para cadastrar uma nova.
              </Typography>
            )}

            {form.form_ids.map((formId, index) => {
              const currentValue = formId === null || formId === undefined ? '' : String(formId);
              return (
                <Stack
                  key={`job-stage-${index}`}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ width: '100%' }}
                >
                  <TextField
                    select
                    fullWidth
                    label={`Etapa ${index + 1}`}
                    value={currentValue}
                    onChange={(e) => handleStageSelectChange(index, e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    disabled={availableForms.length === 0}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <MenuItem value="" disabled>
                      Selecione uma etapa
                    </MenuItem>

                    {/* O valor do <MenuItem> é o ID do formulário (cv_form.id) */}
                    {availableForms.map((stageForm) => {
                      const idStr = String(stageForm.id);
                      const disabled =
                        currentValue !== idStr && selectedStageIdsSet.has(idStr);
                      return (
                        <MenuItem key={idStr} value={idStr} disabled={disabled}>
                          {stageForm.title || `Etapa ${idStr}`}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                    <Tooltip title="Mover para cima">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveStage(index, -1)}
                          disabled={index === 0}
                          aria-label="Mover para cima"
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Mover para baixo">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveStage(index, 1)}
                          disabled={index === form.form_ids.length - 1}
                          aria-label="Mover para baixo"
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Remover etapa">
                      <span>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveStage(index)}
                          aria-label="Remover etapa"
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<PlaylistAdd />}
                onClick={handleAddStage}
                disabled={loadingForms || hasEmptyStageSelection}
              >
                Adicionar etapa
              </Button>
              <Button variant="text" onClick={openCreateStageDialog} disabled={creatingStage}>
                Criar etapa
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Salário - piso (opcional)"
              value={form.salary_floor}
              onFocus={() => handleMoneyFocus('salary_floor')}
              onChange={(e) => handleMoneyChange('salary_floor', e.target.value)}
              onBlur={() => handleMoneyBlur('salary_floor')}
              inputProps={{ inputMode: 'decimal' }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Salário - teto (opcional)"
              value={form.salary_ceiling}
              onFocus={() => handleMoneyFocus('salary_ceiling')}
              onChange={(e) => handleMoneyChange('salary_ceiling', e.target.value)}
              onBlur={() => handleMoneyBlur('salary_ceiling')}
              inputProps={{ inputMode: 'decimal' }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            px: 3,
            py: 1.5,
            bgcolor: 'background.paper',
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Criar Etapa (Atalho) */}
      <Dialog
        open={createStageOpen}
        onClose={handleCloseCreateStageDialog}
        fullWidth
        maxWidth="xs"
        sx={{ '& .MuiDialog-paper': { display: 'flex', maxHeight: '85vh' } }}
      >
        <DialogTitle>Criar nova etapa</DialogTitle>
        <DialogContent
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            mt: 1,
          }}
        >
          <TextField
            label="Título da etapa"
            value={newStageTitle}
            onChange={(e) => setNewStageTitle(e.target.value.slice(0, 200))}
            autoFocus
            disabled={creatingStage}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={handleCloseCreateStageDialog} disabled={creatingStage}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateStage} disabled={creatingStage}>
            {creatingStage ? 'Criando...' : 'Criar etapa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar Exclusão */}
      <ConfirmDialog
        open={Boolean(deleteId)}
        title="Excluir Vaga"
        message={(
          <Stack spacing={1.5}>
            <Alert severity="warning" icon={<WarningAmber fontSize="inherit" />}>
              Atenção: ao excluir esta vaga, todos os candidatos vinculados a ela também serão excluídos.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Esta ação é irreversível e não poderá ser desfeita.
            </Typography>
          </Stack>
        )}
        onConfirm={handleDelete}
        onClose={() => setDeleteId(null)}
      />
    </Box>
  );
}
